import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X } from 'lucide-react';
import { useApi } from './hooks/useApi';
import { useAuth } from './hooks/useAuth';
import { useChatHistory } from './hooks/useChatHistory';
import { useMediaQuery } from './hooks/useMediaQuery';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import Header from './components/Header';
import TabNavigation from './components/TabNavigation';
import ChatManager from './components/ChatManager';
import HomeDashboard from './components/HomeDashboard';
import TrendsDashboard from './components/TrendsDashboard';
import PredictionsDashboard from './components/PredictionsDashboard';
import AboutDashboard from './components/AboutDashboard';
import ContactDashboard from './components/ContactDashboard';
import StorageDashboard from './components/StorageDashboard';
import SystemLoadingScreen from './components/SystemLoadingScreen';
import TutorialManager from './components/TutorialManager';

import ProfileSettings from './components/ProfileSettings';
import Preferences from './components/Preferences';
import LoginPage from './components/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import './styles/theme.css';

// Helper function to generate unique message ID
const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const DISCLAIMER_PATTERNS = [
  /\n*\s*\*{0,2}note\*{0,2}\s*:\s*ai can make mistakes\.[\s\S]*$/i,
  /\n*\s*ai can make mistakes\.[\s\S]*$/i,
];

const normalizeMessageForDedupe = (msg) => {
  const text = typeof msg?.message === 'string' ? msg.message : String(msg?.message || '');
  let normalized = text.trim();
  if (msg?.type === 'ai' && normalized) {
    for (const pattern of DISCLAIMER_PATTERNS) {
      normalized = normalized.replace(pattern, '').trim();
    }
  }
  return normalized.replace(/\s+/g, ' ').toLowerCase();
};

const isAssistantMessage = (msg) => {
  const t = String(msg?.type || '').toLowerCase();
  return t === 'ai' || t === 'assistant' || t === 'bot';
};

// Helper function to deduplicate messages
// Handles ID-based deduplication and only conservative adjacent content dedupe.
const deduplicateMessages = (messages) => {
  const seenById = new Set();
  const unique = [];

  for (const msg of messages) {
    // First, check by ID if message has one
    if (msg.id) {
      if (seenById.has(msg.id)) {
        continue; // Skip duplicate with same ID
      }
      seenById.add(msg.id);
    }

    // Conservative content dedupe: only remove immediate repeated duplicates.
    // This preserves legitimate repeated user queries across history.
    const prev = unique[unique.length - 1];
    if (prev) {
      const prevText = normalizeMessageForDedupe(prev);
      const currText = normalizeMessageForDedupe(msg);
      const sameRole =
        prev.type === msg.type ||
        (isAssistantMessage(prev) && isAssistantMessage(msg));
      if (sameRole && prevText.length > 0 && prevText === currText) {
        continue;
      }
    }

    unique.push(msg);
  }

  return unique;
};

const STARTUP_WARMUP_CONCURRENCY = 2;
const STARTUP_WARMUP_STAGGER_MS = 700;
const RISK_INDICATOR_RETRY_DELAYS_MS = [3000, 8000, 14000];

const isRetryableRiskIndicatorsError = (error) => {
  const status = error?.response?.status ?? null;
  const code = error?.response?.data?.detail?.code || error?.response?.data?.code || null;
  const message = String(
    error?.response?.data?.detail?.error ||
    error?.response?.data?.error ||
    error?.message ||
    ''
  ).toLowerCase();
  if (!status) return true;
  if ([429, 502, 503, 504].includes(status)) return true;
  if (code === 'initializing') return true;
  return message.includes('timeout') || message.includes('network') || message.includes('failed to fetch');
};

function App() {
  const { confirm } = useNotification();
  const [query, setQuery] = useState('');
  // Restore active tab from localStorage, default to 'analytics' (Dashboard-first)
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('activeTab') || 'home';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showChatSidebar, setShowChatSidebar] = useState(() => {
    // In mobile mode, default is Home (chat closed); chat opens only when user clicks
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    if (isMobile) return false;
    const saved = localStorage.getItem('showChatSidebar');
    return saved !== null ? saved === 'true' : true;
  });
  const abortControllerRef = useRef(null);

  const handleToggleChatSidebar = () => {
    setShowChatSidebar((prev) => {
      const next = !prev;
      localStorage.setItem('showChatSidebar', String(next));
      return next;
    });
  };
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const lastMetadataRef = useRef(null);

  // Authentication
  const {
    user,
    token,
    logout,
    isAuthenticated,
    apiClient,
    refreshToken,
    loginWithAzure,
    loading: authLoading,
    error: authError,
    updateProfile,
  } = useAuth();

  // Chat history
  const {
    chats: backendChats,
    loading: chatsLoading,
    createChat: createBackendChat,
    addMessage: addBackendMessage,
    updateChatTitle: updateBackendChatTitle,
    updateChatTitleFromFirstMessage,
    deleteChat: deleteBackendChat,
    clearChat: clearBackendChat,
  } = useChatHistory(token, apiClient);

  // Track if initial chat load has completed
  const [hasInitiallyLoadedChats, setHasInitiallyLoadedChats] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const hasAttemptedInitialChat = useRef(false);
  const hasInitializedFromBackend = useRef(false); // Prevent re-merging during active session

  // Local chat state (synced with backend)
  const [chats, setChats] = useState([]);
  const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('chatSidebarWidth');
    const fallback = 420;
    const parsed = saved ? parseInt(saved, 10) : fallback;
    return Number.isFinite(parsed) ? parsed : fallback;
  });
  const [isResizingChat, setIsResizingChat] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);

  // Track when loading starts
  useEffect(() => {
    if (isAuthenticated && chatsLoading && !hasStartedLoading) {
      setHasStartedLoading(true);
    }
    // Reset flags when logged out
    if (!isAuthenticated) {
      setHasInitiallyLoadedChats(false);
      setHasStartedLoading(false);
      hasAttemptedInitialChat.current = false;
      hasInitializedFromBackend.current = false; // Reset so next login loads from backend
    }
  }, [isAuthenticated, chatsLoading, hasStartedLoading]);

  // Track when initial load completes (loading transitions from true to false)
  useEffect(() => {
    if (isAuthenticated && hasStartedLoading && !chatsLoading && !hasInitiallyLoadedChats) {
      setHasInitiallyLoadedChats(true);
    }
  }, [isAuthenticated, hasStartedLoading, chatsLoading, hasInitiallyLoadedChats]);

  const createInitialChat = useCallback(async () => {
    if (!token) return;
    const newChat = await createBackendChat('New Chat');
    if (newChat) {
      setChats([newChat]);
      setActiveChatId(newChat.id);
    }
  }, [createBackendChat, token]);

  // Sync backend chats with local state
  // IMPORTANT: Only load from backend on INITIAL page load, not during active session
  // This prevents duplicates caused by frontend and backend generating different message IDs

  useEffect(() => {
    if (backendChats && backendChats.length > 0) {
      setChats(prevChats => {
        // Only use backend data if:
        // 1. This is the initial load (no local chats yet), OR
        // 2. We haven't initialized from backend yet
        if (prevChats.length === 0 || !hasInitializedFromBackend.current) {
          hasInitializedFromBackend.current = true;
          // Apply deduplication to clean up any existing duplicates from old bug
          // Also update titles for chats that still have "New Chat" but have messages
          const updatedChats = backendChats.map(chat => {
            const dedupedConversation = deduplicateMessages(chat.conversation);
            let updatedChat = {
              ...chat,
              conversation: dedupedConversation
            };

            // Auto-generate title if still "New Chat" and has messages
            if (chat.title === 'New Chat' && dedupedConversation.length > 0) {
              const firstUserMessage = dedupedConversation.find(msg => msg.type === 'user');
              if (firstUserMessage && firstUserMessage.message) {
                // Update title via backend in background
                updateChatTitleFromFirstMessage(chat.id, firstUserMessage.message);
              }
            }

            return updatedChat;
          });

          return updatedChats;
        }

        // During active session, DON'T merge - keep local state
        // This prevents duplicates from different IDs (frontend vs backend)
        // The backend is being saved separately via addBackendMessage
        return prevChats;
      });

      if (!activeChatId && backendChats.length > 0) {
        setActiveChatId(backendChats[0].id);
      }
      // Reset the flag if we have chats (in case it was set previously)
      hasAttemptedInitialChat.current = false;
    } else if (isAuthenticated && hasInitiallyLoadedChats && !chatsLoading && backendChats.length === 0 && !hasAttemptedInitialChat.current) {
      // Only create initial chat after we've confirmed backend has finished loading and has no chats
      // And only if we haven't already attempted to create one
      hasAttemptedInitialChat.current = true;
      createInitialChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendChats, isAuthenticated, hasInitiallyLoadedChats, chatsLoading, createInitialChat]);


  // Progress state for streaming
  const [processingStatus, setProcessingStatus] = useState(null);

  // Use the custom API hook
  const {
    health,
    charts,
    unitPredictions,
    unitPredictionMeta,
    seasonalAnalysis,
    monthlyTrends,
    heatmapData,
    availableUnits,
    loadingStates,
    fetchChartData,
    fetchIntelligentUnitPrediction,
    setPredictionsTabActive,
    fetchRiskIndicators,
    queueIntelligentPredictions,
    fetchSeasonalAnalysis,
    fetchMonthlyTrends,
    fetchHeatmapData,
    fetchAvailableUnits,
    submitQueryStreaming,
    uploadAndAnalyzeFile,
    checkHealth,
    fetchStats,
    fetchStartupStatus,
    fetchStorageFiles,
    uploadToStorage,
    deleteFromStorage,
    downloadFromStorage,
  } = useApi(apiClient, refreshToken);

  const storageApi = useMemo(() => ({
    fetchStorageFiles,
    uploadToStorage,
    deleteFromStorage,
    downloadFromStorage
  }), [fetchStorageFiles, uploadToStorage, deleteFromStorage, downloadFromStorage]);

  // System loading state
  const [startupStatus, setStartupStatus] = useState(null);
  const [showSystemLoading, setShowSystemLoading] = useState(() => {
    // Only show loading screen if:
    // 1. First time ever (no last visit timestamp)
    // 2. Been inactive for 30+ minutes
    // 3. Backend was restarted (detected later)
    const lastVisit = localStorage.getItem('last_visit_timestamp');
    if (!lastVisit) {
      // First time setup
      return true;
    }

    const lastVisitTime = parseInt(lastVisit, 10);
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000;

    // Show if inactive for 30+ minutes
    return (now - lastVisitTime) > thirtyMinutes;
  });
  const [, setFleetOverviewLoaded] = useState(false);
  const startupPollingRef = useRef(null);
  const startupPollInFlightRef = useRef(false);
  const hasPrefetchedAfterReadyRef = useRef(false);
  const lastTrendsAutoloadRef = useRef(0);
  const predictionsIndicatorsRequestKeyRef = useRef('');
  const predictionsIndicatorsRetryTimerRef = useRef(null);
  const predictionsIndicatorsRetryAttemptRef = useRef(0);

  useEffect(() => {
    return () => {
      if (predictionsIndicatorsRetryTimerRef.current) {
        clearTimeout(predictionsIndicatorsRetryTimerRef.current);
        predictionsIndicatorsRetryTimerRef.current = null;
      }
    };
  }, []);

  // Update last visit timestamp when user is active
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('last_visit_timestamp', Date.now().toString());
    }
  }, [isAuthenticated]);

  // Monitor Fleet Overview loading state
  useEffect(() => {
    // FleetOverview is loaded when we're on Home tab and have overview data
    // Or when we're on a different tab (not waiting for Home data)
    if (activeTab !== 'home' || (activeTab === 'home' && document.querySelector('[data-fleet-overview-loaded="true"]'))) {
      setFleetOverviewLoaded(true);
    }
  }, [activeTab]);

  // Poll startup status on mount
  useEffect(() => {
    if (!isAuthenticated) return;

    // Skip polling if we're not showing the loading screen initially
    // But still check once to detect backend restarts
    let shouldPoll = showSystemLoading;
    let maxTimeoutId = null;

    const pollStartupStatus = async () => {
      if (startupPollInFlightRef.current) return;
      startupPollInFlightRef.current = true;
      try {
        const status = await fetchStartupStatus();
        if (!status || typeof status !== 'object') {
          return;
        }
        setStartupStatus(status);

        // If backend is not ready but we weren't showing loading screen,
        // it means backend restarted - show loading screen
        if (!status.overall_ready && !showSystemLoading) {
          console.log('Backend restart detected, showing loading screen');
          setShowSystemLoading(true);
          hasPrefetchedAfterReadyRef.current = false;
          shouldPoll = true;
        }

        // Dismiss loading screen when backend is ready AND FleetOverview has loaded
        if (status.overall_ready && !startupStatus?.overall_ready && showSystemLoading) {
          // First time seeing overall_ready = true
          // Dismiss immediately without waiting for Fleet Overview (per optimization request)
          console.log('Backend ready, dismissing loading screen');
          setShowSystemLoading(false);
          if (startupPollingRef.current) {
            clearInterval(startupPollingRef.current);
          }
          if (maxTimeoutId) {
            clearTimeout(maxTimeoutId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch startup status:', error);
        // On error, if we're not showing loading screen, don't start polling
      } finally {
        startupPollInFlightRef.current = false;
      }
    };

    // Poll immediately
    pollStartupStatus();

    // Only continue polling if we're showing the loading screen
    if (shouldPoll) {
      // Then poll every 2 seconds until ready
      startupPollingRef.current = setInterval(pollStartupStatus, 2000);

      // Fallback: Force dismiss after 15 seconds maximum
      maxTimeoutId = setTimeout(() => {
        console.log('Loading screen timeout reached, force dismissing');
        setShowSystemLoading(false);
        if (startupPollingRef.current) {
          clearInterval(startupPollingRef.current);
        }
      }, 15000);
    }

    // Cleanup on unmount
    return () => {
      if (startupPollingRef.current) {
        clearInterval(startupPollingRef.current);
      }
      if (maxTimeoutId) {
        clearTimeout(maxTimeoutId);
      }
    };
  }, [isAuthenticated, fetchStartupStatus, startupStatus?.overall_ready, showSystemLoading, activeTab]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Pre-fetch key data once after backend is ready to avoid hard-refresh request storms.
  useEffect(() => {
    if (!isAuthenticated || !startupStatus?.overall_ready) return;
    if (hasPrefetchedAfterReadyRef.current) return;

    hasPrefetchedAfterReadyRef.current = true;
    const skipTrendWarmup = activeTab === 'predictions';
    let cancelled = false;

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const runWithConcurrency = async (tasks, concurrency = STARTUP_WARMUP_CONCURRENCY, staggerMs = STARTUP_WARMUP_STAGGER_MS) => {
      if (!Array.isArray(tasks) || tasks.length === 0) return;
      let cursor = 0;
      const workerCount = Math.max(1, Math.min(concurrency, tasks.length));

      const workers = Array.from({ length: workerCount }, async () => {
        while (!cancelled) {
          const idx = cursor;
          cursor += 1;
          if (idx >= tasks.length) break;
          if (idx > 0 && staggerMs > 0) {
            await wait(staggerMs);
            if (cancelled) break;
          }
          try {
            await tasks[idx]();
          } catch (taskErr) {
            console.warn('Startup warmup task failed:', taskErr);
          }
        }
      });

      await Promise.all(workers);
    };

    const timer = setTimeout(() => {
      if (cancelled) return;
      checkHealth().catch((err) => console.warn('Health check failed after startup ready:', err));
      fetchStats().catch((err) => console.warn('Stats fetch failed after startup ready:', err));
      fetchAvailableUnits(true).catch((err) => console.warn('Available units refresh failed after startup ready:', err));

      if (!skipTrendWarmup) {
        const heavyWarmupTasks = [];
        if (!charts.unit_issues) {
          heavyWarmupTasks.push(() => fetchChartData('unit_issues'));
        }
        if (!charts.monthly_trend) {
          heavyWarmupTasks.push(() => fetchChartData('monthly_trend'));
        }
        if (!charts.planned_issues) {
          heavyWarmupTasks.push(() => fetchChartData('planned_issues'));
        }
        if (!charts.unplanned_issues) {
          heavyWarmupTasks.push(() => fetchChartData('unplanned_issues'));
        }
        if (!monthlyTrends) {
          heavyWarmupTasks.push(() => fetchMonthlyTrends());
        }
        if (!heatmapData) {
          heavyWarmupTasks.push(() => fetchHeatmapData('unplanned'));
        }

        runWithConcurrency(heavyWarmupTasks).catch((err) => {
          console.warn('Startup warmup queue failed:', err);
        });
      }
    }, 1800);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    isAuthenticated,
    startupStatus?.overall_ready,
    activeTab,
    charts,
    monthlyTrends,
    heatmapData,
    checkHealth,
    fetchStats,
    fetchAvailableUnits,
    fetchChartData,
    fetchMonthlyTrends,
    fetchHeatmapData
  ]);

  // Re-fetch units after authentication only when backend is ready.
  useEffect(() => {
    if (!isAuthenticated || !startupStatus?.overall_ready) return;
    fetchAvailableUnits(true).catch((err) => console.warn('Available units refresh failed after auth:', err));
  }, [isAuthenticated, startupStatus?.overall_ready, fetchAvailableUnits]);

  // Load predictions when predictions tab is selected
  useEffect(() => {
    setPredictionsTabActive(activeTab === 'predictions');

    if (activeTab !== 'predictions') {
      predictionsIndicatorsRequestKeyRef.current = '';
      predictionsIndicatorsRetryAttemptRef.current = 0;
      if (predictionsIndicatorsRetryTimerRef.current) {
        clearTimeout(predictionsIndicatorsRetryTimerRef.current);
        predictionsIndicatorsRetryTimerRef.current = null;
      }
      return;
    }
    if (!isAuthenticated || !startupStatus?.overall_ready) return;

    if (!seasonalAnalysis) {
      fetchSeasonalAnalysis(false, { startupReady: true });
    }

    if (availableUnits.length === 0) {
      if (!loadingStates.availableUnits) {
        fetchAvailableUnits(true).catch((err) => console.warn('Available units fetch on predictions tab failed:', err));
      }
      return;
    }

    const unitsKey = availableUnits.join(',');
    if (predictionsIndicatorsRequestKeyRef.current === unitsKey) {
      return;
    }

    predictionsIndicatorsRequestKeyRef.current = unitsKey;

    // Warm per-unit summaries independently from risk-indicator fetch success.
    // This prevents the grid from staying grey when /predict/risk-indicators is retrying.
    if (typeof queueIntelligentPredictions === 'function' && availableUnits.length > 0) {
      queueIntelligentPredictions(availableUnits, {
        detailLevel: 'summary',
        priorityUnits: [availableUnits[0]],
        forceRefresh: false
      });
    }

    fetchRiskIndicators({
      maintenanceType: 'unplanned',
      units: availableUnits,
      forceRefresh: false,
    }).then((payload) => {
      if (payload?.retrying) {
        return;
      }
      predictionsIndicatorsRetryAttemptRef.current = 0;
      if (predictionsIndicatorsRetryTimerRef.current) {
        clearTimeout(predictionsIndicatorsRetryTimerRef.current);
        predictionsIndicatorsRetryTimerRef.current = null;
      }
    }).catch((err) => {
      console.warn('Risk indicators fetch on predictions tab failed:', err);
      predictionsIndicatorsRequestKeyRef.current = '';

      if (!isRetryableRiskIndicatorsError(err)) {
        predictionsIndicatorsRetryAttemptRef.current = 0;
        return;
      }

      const attempt = predictionsIndicatorsRetryAttemptRef.current;
      if (attempt >= RISK_INDICATOR_RETRY_DELAYS_MS.length) {
        return;
      }
      predictionsIndicatorsRetryAttemptRef.current = attempt + 1;

      const delayMs = RISK_INDICATOR_RETRY_DELAYS_MS[attempt];
      if (predictionsIndicatorsRetryTimerRef.current) {
        clearTimeout(predictionsIndicatorsRetryTimerRef.current);
      }
      predictionsIndicatorsRetryTimerRef.current = setTimeout(() => {
        predictionsIndicatorsRequestKeyRef.current = '';
        fetchRiskIndicators({
          maintenanceType: 'unplanned',
          units: availableUnits,
          forceRefresh: true,
        }).catch((retryErr) => {
          console.warn('Risk indicators scheduled retry failed:', retryErr);
        });
      }, delayMs);
    });

  }, [
    activeTab,
    isAuthenticated,
    startupStatus?.overall_ready,
    seasonalAnalysis,
    availableUnits,
    loadingStates.availableUnits,
    fetchSeasonalAnalysis,
    fetchAvailableUnits,
    setPredictionsTabActive,
    fetchRiskIndicators,
    queueIntelligentPredictions,
  ]);

  // Ensure Trends tab self-heals when warmup was skipped or previous fetch returned empty/error.
  useEffect(() => {
    if (activeTab !== 'trends') return;
    if (!isAuthenticated || !startupStatus?.overall_ready) return;
    if (
      loadingStates.monthlyTrends ||
      loadingStates.chart_unit_issues ||
      loadingStates.chart_monthly_trend ||
      loadingStates.heatmap
    ) return;

    const now = Date.now();
    if (now - lastTrendsAutoloadRef.current < 15000) return;

    const monthlyUnits = monthlyTrends?.unit_highlights?.length || 0;
    const monthlySeries = Object.keys(monthlyTrends?.unit_monthly_data || {}).length;
    const missingMonthlyTrends = (
      !monthlyTrends ||
      monthlyTrends.error ||
      monthlyTrends.initializing ||
      monthlyUnits === 0 ||
      monthlySeries === 0
    );
    const missingUnitIssuesChart = !charts?.unit_issues || charts.unit_issues?.error;
    const missingMonthlyTrendChart = !charts?.monthly_trend || charts.monthly_trend?.error;
    const missingHeatmap = !heatmapData || heatmapData?.error;

    if (!missingMonthlyTrends && !missingUnitIssuesChart && !missingMonthlyTrendChart && !missingHeatmap) return;

    lastTrendsAutoloadRef.current = now;
    if (missingMonthlyTrends) {
      fetchMonthlyTrends(true, { skipDebounce: true }).catch((err) =>
        console.warn('Trends auto-load (monthly trends) failed:', err)
      );
    }
    if (missingUnitIssuesChart) {
      fetchChartData('unit_issues', null, true, { skipDebounce: true }).catch((err) =>
        console.warn('Trends auto-load (unit issues chart) failed:', err)
      );
    }
    if (missingMonthlyTrendChart) {
      fetchChartData('monthly_trend', null, true, { skipDebounce: true }).catch((err) =>
        console.warn('Trends auto-load (monthly trend chart) failed:', err)
      );
    }
    if (missingHeatmap) {
      fetchHeatmapData('unplanned', true, { skipDebounce: true }).catch((err) =>
        console.warn('Trends auto-load (heatmap) failed:', err)
      );
    }
  }, [
    activeTab,
    isAuthenticated,
    startupStatus?.overall_ready,
    loadingStates.monthlyTrends,
    loadingStates.chart_unit_issues,
    loadingStates.chart_monthly_trend,
    loadingStates.heatmap,
    monthlyTrends,
    charts,
    heatmapData,
    fetchMonthlyTrends,
    fetchChartData,
    fetchHeatmapData,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loadingStates.query || isSubmitting) return;

    // Check if there's a file to upload (handled separately)
    // Regular query handling continues below
    if (!query.trim()) return;

    // Ensure there's an active chat before sending a message.
    if (!activeChatId || chats.length === 0) {
      await createNewChat();
      // Wait a moment for state to update before proceeding
      await new Promise(resolve => setTimeout(resolve, 50));
      // Re-check activeChatId after creating chat
      if (!activeChatId) {
        console.warn('Failed to create active chat, cannot send message');
        return;
      }
    }

    const userMessage = query;
    setQuery('');
    setIsSubmitting(true);
    setProcessingStatus(null);
    lastMetadataRef.current = null; // Reset metadata for new query

    // Check auto-save preference
    const autoSave = localStorage.getItem('chat.autoSave') !== 'false';
    const showTimestamps = localStorage.getItem('chat.showTimestamps') === 'true';

    // Create timestamp if timestamps are enabled
    const timestamp = showTimestamps ? new Date().toISOString() : null;

    // Add user message immediately to show in chat
    const userMessageId = generateMessageId();
    const isFirstMessage = chats.find(c => c.id === activeChatId)?.conversation.length === 0;
    const autoGeneratedTitle = isFirstMessage
      ? userMessage.substring(0, 30) + (userMessage.length > 30 ? '...' : '')
      : null;

    setChats(prevChats => prevChats.map(chat =>
      chat.id === activeChatId
        ? {
          ...chat,
          conversation: [...chat.conversation,
          { id: userMessageId, type: 'user', message: userMessage, timestamp: timestamp }
          ],
          title: autoGeneratedTitle || chat.title
        }
        : chat
    ));

    // Update backend chat title if this is the first message
    if (autoGeneratedTitle && autoSave && token) {
      updateBackendChatTitle(activeChatId, autoGeneratedTitle).catch(err =>
        console.warn('Failed to update chat title:', err)
      );
    }

    // Do not persist via /chat/chats/{id}/messages here.
    // /chat/stream already persists the full conversation state in the backend engine.

    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Get current conversation history (before the new user message was added)
    // This provides context for follow-up questions
    const activeChat = chats.find(chat => chat.id === activeChatId);
    const conversationHistory = activeChat?.conversation || [];

    // Use streaming for real-time progress updates
    let terminalHandled = false; // Prevent duplicate AI bubbles from repeated terminal SSE callbacks.
    await submitQueryStreaming(
      userMessage,
      // onProgress callback
      (progress) => {
        setProcessingStatus(progress);
        // Accumulate metadata if sent via progress status events
        if (progress && progress.metadata) {
          lastMetadataRef.current = progress.metadata;
        }
      },
      // onComplete callback
      async (response, metadata) => {
        if (terminalHandled) {
          console.warn('Duplicate onComplete suppressed for chat request');
          return;
        }
        terminalHandled = true;
        setProcessingStatus(null);

        // Use accumulated metadata as fallback if final response metadata is empty
        const finalMetadata = metadata || lastMetadataRef.current;

        let responseText = typeof response === 'string' ? response : JSON.stringify(response, null, 2);

        // Check preferences and if user is temporary
        const showTimestamps = localStorage.getItem('chat.showTimestamps') === 'true';
        const timestamp = showTimestamps ? new Date().toISOString() : null;

        // Add AI response to local state (with insertion-time duplicate guard)
        const aiMessageId = generateMessageId();
        setChats(prevChats => prevChats.map(chat =>
          chat.id !== activeChatId
            ? chat
            : (() => {
              const lastMsg = chat.conversation?.[chat.conversation.length - 1];
              const isDuplicateAssistant =
                isAssistantMessage(lastMsg) &&
                normalizeMessageForDedupe(lastMsg) === normalizeMessageForDedupe({ type: 'ai', message: responseText });
              if (isDuplicateAssistant) {
                return chat;
              }
              return {
                ...chat,
                conversation: [...chat.conversation,
                { id: aiMessageId, type: 'ai', message: responseText, timestamp: timestamp, metadata: finalMetadata }
                ]
              };
            })()
        ));

        // Clear submitting state immediately after response is displayed
        setIsSubmitting(false);

        // No extra /chat/chats/{id}/messages persistence here.
        // The streaming backend already persisted this assistant message.
      },
      // onError callback
      async (error) => {
        if (terminalHandled) {
          console.warn('Duplicate onError suppressed for chat request');
          return;
        }
        terminalHandled = true;
        setProcessingStatus(null);
        let errorMessage = error || 'Sorry, there was an error processing your query. Please try again.';

        // Check preferences and if user is temporary
        const showTimestamps = localStorage.getItem('chat.showTimestamps') === 'true';
        const timestamp = showTimestamps ? new Date().toISOString() : null;

        // Add error message to local state (with insertion-time duplicate guard)
        const errorMessageId = generateMessageId();
        setChats(prevChats => prevChats.map(chat =>
          chat.id !== activeChatId
            ? chat
            : (() => {
              const lastMsg = chat.conversation?.[chat.conversation.length - 1];
              const isDuplicateAssistant =
                isAssistantMessage(lastMsg) &&
                normalizeMessageForDedupe(lastMsg) === normalizeMessageForDedupe({ type: 'ai', message: errorMessage });
              if (isDuplicateAssistant) {
                return chat;
              }
              return {
                ...chat,
                conversation: [...chat.conversation,
                { id: errorMessageId, type: 'ai', message: errorMessage, timestamp: timestamp }
                ]
              };
            })()
        ));

        // Clear submitting state immediately after response is displayed
        setIsSubmitting(false);

        // No extra /chat/chats/{id}/messages persistence here.
        // The streaming backend already persisted this assistant/error message.
      },
      // Pass auth token
      token,
      // Pass abort controller
      abortController,
      // Pass conversation history for context (still useful for local UI mainly)
      conversationHistory,
      // Pass activeChatId as conversationId for backend memory
      activeChatId
    );

    // Clear abort controller reference after completion
    if (abortControllerRef.current === abortController) {
      abortControllerRef.current = null;
    }
  };

  // Cancel current request
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsSubmitting(false);
      setProcessingStatus(null);
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileUpload = async (file, userQuery = '') => {
    if (!file) return;

    // Clear query immediately when upload starts
    setQuery('');

    setIsSubmitting(true);
    setProcessingStatus({ message: 'Uploading file...', step: 10 });

    // Check auto-save preference
    const autoSave = localStorage.getItem('chat.autoSave') !== 'false';
    const showTimestamps = localStorage.getItem('chat.showTimestamps') === 'true';
    const timestamp = showTimestamps ? new Date().toISOString() : null;

    // Create file attachment info
    const attachment = {
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop()
    };

    // Add user message about file upload (with attachment info)
    const uploadMessage = userQuery || 'Please analyze this file';

    const uploadMessageId = generateMessageId();
    const isFirstMessage = chats.find(c => c.id === activeChatId)?.conversation.length === 0;
    const autoGeneratedTitle = isFirstMessage
      ? file.name.substring(0, 30) + (file.name.length > 30 ? '...' : '')
      : null;

    setChats(prevChats => prevChats.map(chat =>
      chat.id === activeChatId
        ? {
          ...chat,
          conversation: [...chat.conversation,
          { id: uploadMessageId, type: 'user', message: uploadMessage, timestamp: timestamp, attachment: attachment }
          ],
          title: autoGeneratedTitle || chat.title
        }
        : chat
    ));

    // Update backend chat title if this is the first message
    if (autoGeneratedTitle && autoSave && token) {
      updateBackendChatTitle(activeChatId, autoGeneratedTitle).catch(err =>
        console.warn('Failed to update chat title:', err)
      );
    }

    // Save user message to backend (include file name in message for persistence)
    if (autoSave && token && activeChatId) {
      await addBackendMessage(activeChatId, 'user', `[File: ${file.name}] ${uploadMessage}`, uploadMessageId);
    }

    try {
      setProcessingStatus({ message: 'Processing file...', step: 30 });

      const response = await uploadAndAnalyzeFile(file, userQuery, token);

      setProcessingStatus(null);

      // Add AI response
      let responseText = typeof response === 'string' ? response : JSON.stringify(response, null, 2);

      const fileResponseId = generateMessageId();
      setChats(prevChats => prevChats.map(chat =>
        chat.id === activeChatId
          ? {
            ...chat,
            conversation: [...chat.conversation,
            { id: fileResponseId, type: 'ai', message: responseText, timestamp: timestamp }
            ]
          }
          : chat
      ));

      // Save AI response to backend
      if (autoSave && token && activeChatId) {
        await addBackendMessage(activeChatId, 'ai', responseText, fileResponseId);
      }
    } catch (error) {
      setProcessingStatus(null);
      let errorMessage = error.message || 'Sorry, there was an error processing your file. Please try again.';

      const fileErrorId = generateMessageId();
      setChats(prevChats => prevChats.map(chat =>
        chat.id === activeChatId
          ? {
            ...chat,
            conversation: [...chat.conversation,
            { id: fileErrorId, type: 'ai', message: errorMessage, timestamp: timestamp }
            ]
          }
          : chat
      ));

      if (autoSave && token && activeChatId) {
        await addBackendMessage(activeChatId, 'ai', errorMessage, fileErrorId);
      }
    } finally {
      setIsSubmitting(false);
      // Clear the query input after file upload
      setQuery('');
    }
  };

  const clearConversation = async () => {
    if (token && activeChatId) {
      await clearBackendChat(activeChatId);
    }
    setChats(prevChats => prevChats.map(chat =>
      chat.id === activeChatId
        ? { ...chat, conversation: [] }
        : chat
    ));
  };

  const handleLogin = (userData) => {
    // User is already set by useAuth hook after successful login
    // This callback is just for compatibility with LoginPage
  };

  const handleUpdateUser = (updatedUser) => {
    // User is already updated by useAuth hook
    // This callback is just for compatibility with ProfileSettings
  };

  // Show login modal if not authenticated (but keep the app structure visible in background)
  const showLoginModal = !isAuthenticated;

  const createNewChat = async () => {
    if (!token) return;
    const newChat = await createBackendChat('New Chat');
    if (newChat) {
      // Update existing chats to set isActive to false
      // Preserve all existing chat conversations
      const updatedChats = chats.map(chat => ({ ...chat, isActive: false }));
      // Add new chat with isActive: true
      setChats([{ ...newChat, isActive: true }, ...updatedChats]);
      setActiveChatId(newChat.id);
    }
  };

  const switchChat = (chatId) => {
    setActiveChatId(chatId);
    // Update active status while preserving all conversation data
    setChats(prevChats => prevChats.map(chat => ({
      ...chat,
      isActive: chat.id === chatId
    })));
  };

  const deleteChat = async (chatId) => {
    // Add confirmation before deleting
    const ok = await confirm({
      title: 'Delete Chat Thread',
      message: 'Are you sure you want to delete this chat thread? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      tone: 'danger'
    });
    if (!ok) {
      return;
    }

    // Delete from backend first
    if (token) {
      await deleteBackendChat(chatId);
    }

    if (chats.length === 1) {
      // Don't delete the last chat, create a new one
      const newChat = await createBackendChat('New Chat');
      if (newChat) {
        setChats([newChat]);
        setActiveChatId(newChat.id);
      }
    } else {
      const updatedChats = chats.filter(chat => chat.id !== chatId);
      const newActiveChat = updatedChats[0];
      setChats(updatedChats.map(chat => ({
        ...chat,
        isActive: chat.id === newActiveChat.id
      })));
      setActiveChatId(newActiveChat.id);
    }
  };

  useEffect(() => {
    if (!isResizingChat) return;

    const handleMouseMove = (e) => {
      const minWidth = 320;
      const maxWidth = 640;
      const next = Math.min(maxWidth, Math.max(minWidth, window.innerWidth - e.clientX - 24));
      setChatSidebarWidth(next);
    };

    const handleMouseUp = () => {
      setIsResizingChat(false);
      localStorage.setItem('chatSidebarWidth', String(chatSidebarWidth));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingChat, chatSidebarWidth]);

  return (
    <div className="min-h-screen bg-gray-50">
      <TutorialManager
        setActiveTab={setActiveTab}
        activeTab={activeTab}
      />
      {/* System Loading Screen - Shows during backend startup */}
      {isAuthenticated && showSystemLoading && startupStatus && (
        <SystemLoadingScreen status={startupStatus} />
      )}

      {/* Login Modal - Shows when not authenticated */}
      {showLoginModal && (
        <LoginPage
          onLogin={handleLogin}
          loginWithAzure={loginWithAzure}
          loading={authLoading}
          authError={authError}
          isOpen={true}
          onClose={null} // Don't allow closing when not authenticated
        />
      )}

      {/* Main App Content - Blurred when login modal is open */}
      <div className={showLoginModal ? 'filter blur-sm pointer-events-none' : ''}>
        {/* Header */}
        <Header
          health={health}
          user={user}
          showChatSidebar={showChatSidebar}
          onToggleChat={handleToggleChatSidebar}
          onLogout={logout}
          onProfileSettings={() => setShowProfileSettings(true)}
          onPreferences={() => setShowPreferences(true)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          {/* Tab Navigation - hidden on mobile (hamburger dropdown in header uses logo) */}
          <div className="hidden md:block">
            <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>


          {/* Main Layout Grid */}
          <div className="flex flex-col lg:flex-row gap-6">

            {/* Left Content Area (Tab Content) - scroll horizontally if content overflows on small screens */}
            <div className="flex-1 min-w-0 overflow-x-auto overflow-y-visible">
              <div key={activeTab} className="page-transition">
                {activeTab === 'home' && (
                  <HomeDashboard
                    uploadToStorage={uploadToStorage}
                    apiClient={apiClient}
                    isAuthenticated={isAuthenticated}
                  />
                )}

                {activeTab === 'trends' && (
                  <TrendsDashboard
                    charts={charts}
                    fetchChartData={fetchChartData}
                    monthlyTrends={monthlyTrends}
                    fetchMonthlyTrends={fetchMonthlyTrends}
                    heatmapData={heatmapData}
                    fetchHeatmapData={fetchHeatmapData}
                    loadingStates={loadingStates}
                    apiClient={apiClient}
                  />
                )}

                {activeTab === 'predictions' && (
                  <PredictionsDashboard
                    unitPredictions={unitPredictions}
                    unitPredictionMeta={unitPredictionMeta}
                    fetchIntelligentUnitPrediction={fetchIntelligentUnitPrediction}
                    seasonalAnalysis={seasonalAnalysis}
                    availableUnits={availableUnits}
                    loadingStates={loadingStates}
                    uploadToStorage={uploadToStorage}
                    onUsePrompt={(promptText) => {
                      setShowChatSidebar(true);
                      setQuery(promptText);
                      const chatInput = document.getElementById('chat-input-area');
                      if (chatInput) chatInput.scrollIntoView({ behavior: 'smooth' });
                    }}
                  />
                )}

                {activeTab === 'storage' && (
                  <StorageDashboard
                    api={storageApi}
                  />
                )}

                {activeTab === 'about' && (
                  <AboutDashboard setActiveTab={setActiveTab} />
                )}

                {activeTab === 'contact' && (
                  <ContactDashboard user={user} token={token} />
                )}
              </div>
              <footer
                className="mt-8 pt-4 pb-2 border-t text-center"
                style={{ borderColor: 'var(--card-border)' }}
              >
                <p className="text-xs" style={{ color: 'var(--accent-text-subtle)' }}>
                  &copy; {new Date().getFullYear()} bravus.com.au. All rights reserved.
                </p>
              </footer>
            </div>

            {/* Right Sidebar: Chat on large screens only */}
            {showChatSidebar && isLargeScreen && (
              <>
                <div
                  className="hidden lg:flex items-stretch"
                  style={{ marginRight: 6 }}
                >
                  <div
                    onMouseDown={() => setIsResizingChat(true)}
                    className="w-2 cursor-col-resize rounded-full bg-transparent hover:bg-gray-200/70 transition-colors flex items-center justify-center"
                    title="Drag to resize chat panel"
                    aria-label="Resize chat panel"
                  >
                    <span className="block w-px h-full bg-gray-200" />
                  </div>
                </div>
                <div
                  className="w-full flex-shrink-0 lg:sticky lg:top-6 lg:self-start lg:h-[calc(100vh-6rem)] lg:max-h-[calc(100vh-6rem)] animate-fade-in-up delay-500 min-w-0 pl-0 lg:pl-2"
                  style={{ width: `${chatSidebarWidth}px` }}
                >
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[400px] h-[60vh] sm:h-[65vh] lg:h-full lg:min-h-0 flex flex-col overflow-hidden p-3 sm:p-4 border-l-4" style={{ borderLeftColor: 'var(--accent-primary)' }}>
                    <div className="flex-1 min-h-0 overflow-hidden relative flex flex-col">
                      <ChatManager
                        loading={loadingStates.query || isSubmitting}
                        chatsLoading={chatsLoading}
                        processingStatus={processingStatus}
                        query={query}
                        setQuery={setQuery}
                        handleSubmit={handleSubmit}
                        handleKeyPress={handleKeyPress}
                        chats={chats}
                        activeChatId={activeChatId}
                        createNewChat={createNewChat}
                        switchChat={switchChat}
                        deleteChat={deleteChat}
                        clearConversation={clearConversation}
                        handleFileUpload={handleFileUpload}
                        cancelRequest={cancelRequest}
                        apiClient={apiClient}
                        isSidebarMode={true}
                        isAuthenticated={isAuthenticated}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

          </div>


        </div>

        {/* Chat modal: small screens only (lg+ uses sidebar) */}
        {showChatSidebar && !isLargeScreen && (
          <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
            <div className="flex-shrink-0 h-14 px-4 flex items-center justify-between border-b border-gray-200 bg-white">
              <span className="text-base font-semibold text-gray-900">Chat</span>
              <button
                type="button"
                onClick={() => setShowChatSidebar(false)}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Close chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-3 sm:p-4">
              <ChatManager
                loading={loadingStates.query || isSubmitting}
                chatsLoading={chatsLoading}
                processingStatus={processingStatus}
                query={query}
                setQuery={setQuery}
                handleSubmit={handleSubmit}
                handleKeyPress={handleKeyPress}
                chats={chats}
                activeChatId={activeChatId}
                createNewChat={createNewChat}
                switchChat={switchChat}
                deleteChat={deleteChat}
                clearConversation={clearConversation}
                handleFileUpload={handleFileUpload}
                cancelRequest={cancelRequest}
                apiClient={apiClient}
                isSidebarMode={true}
                isAuthenticated={isAuthenticated}
              />
            </div>
          </div>
        )}

        {/* Profile Settings Modal */}
        {showProfileSettings && (
          <ProfileSettings
            user={user}
            updateProfile={updateProfile}
            loading={authLoading}
            onClose={() => setShowProfileSettings(false)}
            onUpdate={handleUpdateUser}
          />
        )}

        {/* Preferences Modal */}
        {showPreferences && (
          <Preferences
            user={user}
            apiClient={apiClient}
            onClose={() => setShowPreferences(false)}
            onUpdate={(prefs) => console.log('Preferences updated:', prefs)}
          />
        )}
      </div>
    </div>
  );
}

export default function AppWithTheme() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </NotificationProvider>
    </ThemeProvider>
  );
}
